export type RuntimeEnvLabel = "local" | "staging" | "live";

/** BE 타깃 환경 (테마·헤더 색상 결정용) */
export type BeTargetEnv = "staging" | "live";

export const LIVE_HOSTS = [
  "control.ggcontent.com",
  "control2.ggcontent.com",
] as const;
export const STAGING_HOSTS = [
  "control2-staging.ggcontent.com",
  "gmchanger-admin-dev.herokuapp.com",
] as const;

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "prod.localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".local")
  );
}

/**
 * v2 production BE origin. `api-staging.ggcontent.com` contains the substring
 * `api.ggcontent.com`, so the staging host must be excluded explicitly.
 */
function isV2LiveTarget(v2Target: string): boolean {
  return v2Target.includes("api.ggcontent.com") && !v2Target.includes("api-staging");
}

/**
 * v2 BE 타깃(브라우저에 노출되는 v2 프록시/베이스 URL)을 읽어온다.
 * - localhost dev: VITE_NEW_API_PROXY_TARGET (dev=api-staging, dev:production=api)
 * - deployed build: VITE_NEW_API_BASE_URL
 */
function readV2Target(): string {
  return (
    import.meta.env.VITE_NEW_API_BASE_URL ??
    import.meta.env.VITE_NEW_API_PROXY_TARGET ??
    ""
  );
}

/**
 * 런타임 호스트와 v2 BE 타깃을 동일한 기준으로 해석해 환경 라벨을 정한다.
 * (레거시 VITE_PROXY_TARGET 의존 제거 — v2-only.)
 */
export function detectRuntimeEnv(
  hostname: string,
  v2Target = "",
): RuntimeEnvLabel {
  if (isLocalHost(hostname)) return "local";
  if (LIVE_HOSTS.includes(hostname as (typeof LIVE_HOSTS)[number]))
    return "live";
  if (STAGING_HOSTS.includes(hostname as (typeof STAGING_HOSTS)[number]))
    return "staging";
  if (isV2LiveTarget(v2Target)) return "live";

  return "staging";
}

/**
 * v2 BE 타깃으로 BE 환경을 판별 (localhost에서 테마 결정용).
 * localhost가 아닌 경우 hostname 기반.
 */
export function detectBeTargetEnv(
  hostname: string,
  v2Target = "",
): BeTargetEnv {
  if (isLocalHost(hostname)) {
    return isV2LiveTarget(v2Target) ? "live" : "staging";
  }
  if (LIVE_HOSTS.includes(hostname as (typeof LIVE_HOSTS)[number]))
    return "live";
  return "staging";
}

export function detectRuntimeEnvFromBrowser(): RuntimeEnvLabel {
  if (typeof window === "undefined") return "live";

  return detectRuntimeEnv(window.location.hostname, readV2Target());
}

export function detectBeTargetEnvFromBrowser(): BeTargetEnv {
  if (typeof window === "undefined") return "live";

  return detectBeTargetEnv(window.location.hostname, readV2Target());
}

export function isNonProductionRuntime(): boolean {
  return detectRuntimeEnvFromBrowser() !== "live";
}
